import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';
import {
  User,
  Settings,
  Wrench,
  Plus,
  Upload,
  FileUp,
  Database,
  Search,
  ChevronDown,
  ChevronUp,
  Bell,
  MessageSquare,
  Check
} from 'lucide-react';

const getDisciplineFromPath = (pathname) => {
  if (pathname.startsWith('/3d-printing')) return '3d_printing';
  if (pathname.startsWith('/laser')) return 'laser_cutting';
  return 'cnc_routing';
};

const getEditionPrefix = (pathname) => {
  if (pathname.startsWith('/3d-printing')) return '/3d-printing';
  if (pathname.startsWith('/laser')) return '/laser';
  return '';
};

const Profile = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = localStorage.getItem('token');

  const currentDiscipline = getDisciplineFromPath(location.pathname);
  const editionPrefix = getEditionPrefix(location.pathname);
  const defaultTab = searchParams.get('tab') || 'account';

  const [machines, setMachines] = useState([]);
  const [allTools, setAllTools] = useState([]);
  const [toolSearchQuery, setToolSearchQuery] = useState('');
  const [toolCategoryFilter, setToolCategoryFilter] = useState('all');
  const [expandedToolId, setExpandedToolId] = useState(null);

  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState({ total: 0, unread: 0 });
  const [selectedNotification, setSelectedNotification] = useState(null);

  const [myFeedback, setMyFeedback] = useState([]);
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  useEffect(() => {
    fetchMachines();
    fetchNotifications();
    fetchMyFeedback();
  }, []);

  const fetchMachines = async () => {
    try {
      const res = await axios.get(`${API}/machines`);
      setMachines(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchNotifications = async () => {
    if (!token) return;
    try {
      const [notifRes, countRes] = await Promise.all([
        axios.get(`${API}/notifications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/notifications/count`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setNotifications(notifRes.data);
      setNotificationCount(countRes.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchMyFeedback = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/beta-feedback/my-feedback`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyFeedback(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
      toast.success('All notifications marked as read');
    } catch {
      toast.error('Failed to mark notifications as read');
    }
  };

  const toolCategories = [...new Set(allTools.map(t => t.category).filter(Boolean))];

  const filteredTools = allTools.filter(tool => {
    const matchesSearch =
      !toolSearchQuery ||
      tool.name?.toLowerCase().includes(toolSearchQuery.toLowerCase());

    const matchesCategory =
      toolCategoryFilter === 'all' || tool.category === toolCategoryFilter;

    return matchesSearch && matchesCategory;
  });

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <h1 className="text-4xl font-black">
        My <span className="text-[#B45309]">Profile</span>
      </h1>

      <Tabs defaultValue={defaultTab}>
        <TabsList className="grid grid-cols-5">
          <TabsTrigger value="account"><User className="w-4 h-4 mr-2" />Account</TabsTrigger>
          <TabsTrigger value="machines"><Settings className="w-4 h-4 mr-2" />Machines</TabsTrigger>
          <TabsTrigger value="tools"><Wrench className="w-4 h-4 mr-2" />Tools</TabsTrigger>
          <TabsTrigger value="gcode"><FileUp className="w-4 h-4 mr-2" />G-Code</TabsTrigger>
          <TabsTrigger value="notifications">
            <Bell className="w-4 h-4 mr-2" />
            Messages
            {notificationCount.unread > 0 && (
              <span className="ml-2 bg-orange-500 text-white text-xs rounded-full px-2">
                {notificationCount.unread}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ===================== */}
        {/* NOTIFICATIONS TAB */}
        {/* ===================== */}
        <TabsContent value="notifications">
          <div className="space-y-6">

            {/* Developer Messages */}
            <Card>
              <CardHeader>
                <div className="flex justify-between">
                  <CardTitle>Developer Messages</CardTitle>
                  {notificationCount.unread > 0 && (
                    <Button size="sm" onClick={markAllNotificationsRead}>
                      <Check className="w-4 h-4 mr-2" />
                      Mark All Read
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {notifications.length === 0 ? (
                  <p className="text-sm text-gray-500">No messages yet</p>
                ) : (
                  notifications.map(n => (
                    <div
                      key={n.id}
                      className="border rounded mb-3 overflow-hidden cursor-pointer"
                      onClick={() =>
                        setSelectedNotification(
                          selectedNotification === n.id ? null : n.id
                        )
                      }
                    >
                      <div className={`p-3 flex justify-between items-start ${!n.read ? 'bg-orange-50' : ''}`}>
                        <div>
                          <div className="font-semibold text-sm">{n.title}</div>
                          {selectedNotification !== n.id && (
                            <div className="text-sm text-gray-500 mt-1 line-clamp-1">
                              {n.message}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                          {!n.read && (
                            <span className="w-2 h-2 rounded-full bg-orange-500 inline-block" />
                          )}
                          <span className="text-gray-400 text-xs">
                            {selectedNotification === n.id ? '▲' : '▼'}
                          </span>
                        </div>
                      </div>
                      {selectedNotification === n.id && (
                        <div className="px-3 pb-3 pt-1 bg-orange-50 border-t border-orange-100">
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{n.message}</p>
                          <p className="text-xs text-gray-400 mt-2">
                            {new Date(n.created_at).toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* ===================== */}
            {/* MY FEEDBACK HISTORY */}
            {/* ===================== */}
            <Card>
              <CardHeader>
                <CardTitle>My Feedback History</CardTitle>
                <CardDescription>
                  Click a feedback entry to view developer response
                </CardDescription>
              </CardHeader>
              <CardContent>
                {myFeedback.length === 0 ? (
                  <p className="text-sm text-gray-500">No feedback submitted yet</p>
                ) : (
                  myFeedback.map(feedback => (
                    <div
                      key={feedback.id}
                      className="p-4 border rounded mb-4 cursor-pointer bg-white/60"
                      onClick={() =>
                        setSelectedFeedback(
                          selectedFeedback === feedback.id ? null : feedback.id
                        )
                      }
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-semibold">{feedback.subject}</div>
                          <div className="text-xs text-gray-500">
                            {new Date(feedback.submitted_at).toLocaleDateString()}
                          </div>
                        </div>

                        {feedback.admin_response?.response_text && (
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            Replied
                          </Badge>
                        )}
                      </div>

                      {/* Expanded Section */}
                      {selectedFeedback === feedback.id && (
                        <div className="mt-4 space-y-4">
                          <div>
                            <div className="text-xs font-bold text-gray-500 mb-1">
                              Your Message
                            </div>
                            <div className="text-sm">
                              {feedback.message}
                            </div>
                          </div>

                          {feedback.admin_response?.response_text ? (
                            <div className="bg-orange-50 border border-orange-200 p-3 rounded">
                              <div className="text-xs font-bold text-orange-700 mb-1">
                                Developer Reply
                              </div>
                              <div className="text-sm">
                                {feedback.admin_response.response_text}
                              </div>
                            </div>
                          ) : (
                            <div className="text-xs text-gray-400 italic">
                              Awaiting developer response...
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Profile;
